/**
 * Browser-side TRON connect + TRC20 transfer.
 * Targets TronLink (extension or in-app browser inside TronLink Mobile) and Trust Wallet.
 * Uses injected TronWeb / window.tronLink without needing external heavy SDK.
 */

export interface ConnectedTronWallet {
  chain: 'tron';
  address: string;
  walletLabel: string;
}

export interface TronPayParams {
  to: string;
  amount: number;
  currency: 'USDT' | 'TRX';
}

export interface TronPayResult {
  txHash: string;
  fromAddress: string;
  walletLabel: string;
  chain: 'tron';
}

// TRC20 USDT Tether contract on Tron Mainnet
export const USDT_TRC20_CONTRACT = 'TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t';

interface TronLinkProvider {
  ready?: boolean;
  request(opts: { method: string }): Promise<unknown>;
  isTronLink?: boolean;
  isTrust?: boolean;
  isTrustWallet?: boolean;
  tronlinkParams?: unknown;
}

interface TronWebInstance {
  defaultAddress?: { base58?: string };
  ready?: boolean;
  trx?: {
    sendTransaction(to: string, amountSun: number): Promise<{ txid?: string; result?: boolean }>;
  };
  contract?: () => {
    at(addr: string): Promise<{
      transfer(to: string, amount: string | number): {
        send(opts?: Record<string, unknown>): Promise<string>;
      };
    }>;
  };
}

declare global {
  interface Window {
    tronLink?: TronLinkProvider;
    tronWeb?: TronWebInstance;
  }
}

export function isTronAvailable(): boolean {
  return typeof window !== 'undefined' && !!window.tronLink;
}

export function detectTronWalletLabel(): string {
  if (typeof window === 'undefined') return 'Tron Wallet';
  const p = window.tronLink;
  if (p?.isTrust || p?.isTrustWallet) return 'Trust Wallet';
  if (p?.isTronLink || p?.tronlinkParams) return 'TronLink';
  return 'Tron Wallet';
}

export function isValidTronAddress(address: string): boolean {
  return typeof address === 'string' && /^T[1-9A-HJ-NP-Za-km-z]{33}$/.test(address);
}

export function truncateAddress(addr: string): string {
  if (!addr) return '';
  if (addr.length <= 12) return addr;
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

async function waitForTronReady(timeoutMs = 4000): Promise<void> {
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    if (typeof window !== 'undefined' && window.tronWeb?.ready && window.tronWeb.defaultAddress?.base58) {
      return;
    }
    await new Promise((r) => setTimeout(r, 100));
  }
}

export async function connectTron(): Promise<ConnectedTronWallet> {
  if (typeof window === 'undefined' || !window.tronLink) {
    throw new Error('TronLink is not installed. Visit https://www.tronlink.org');
  }

  try {
    await window.tronLink.request({ method: 'tron_requestAccounts' });
  } catch (err: any) {
    if (err?.code === 4001 || /reject|denied|cancel/i.test(err?.message || '')) {
      throw new Error('Connection rejected by user');
    }
    throw err instanceof Error ? err : new Error(String(err));
  }

  await waitForTronReady();
  const address = window.tronWeb?.defaultAddress?.base58;
  if (!address) {
    throw new Error('Failed to get active TRON address');
  }

  return {
    chain: 'tron',
    address,
    walletLabel: detectTronWalletLabel(),
  };
}

export function openInTronLinkApp(): void {
  if (typeof window === 'undefined') return;
  const param = encodeURIComponent(
    JSON.stringify({
      url: window.location.href,
      action: 'open',
      protocol: 'TronLink',
      version: '1.0',
    })
  );
  window.location.href = `tronlinkoutside://pull.activity?param=${param}`;
}

export async function payTron(params: TronPayParams): Promise<TronPayResult> {
  if (typeof window === 'undefined' || !window.tronWeb) {
    throw new Error('TronLink is not active.');
  }

  await waitForTronReady();
  const tronWeb = window.tronWeb;
  const fromAddress = tronWeb.defaultAddress?.base58;
  if (!fromAddress) throw new Error('Tron wallet is not connected');

  try {
    if (params.currency === 'TRX') {
      const sun = Math.round(params.amount * 1_000_000);
      if (!tronWeb.trx) throw new Error('TronWeb TRX API is unavailable');
      const res = await tronWeb.trx.sendTransaction(params.to, sun);
      const txid = res?.txid;
      if (!txid) throw new Error('TronLink returned no txid');
      return { txHash: txid, fromAddress, walletLabel: detectTronWalletLabel(), chain: 'tron' };
    }

    if (params.currency === 'USDT') {
      const decimals = 6;
      const raw = BigInt(Math.round(params.amount * 10 ** decimals)).toString();
      if (!tronWeb.contract) throw new Error('TronWeb Contract API is unavailable');
      const contract = await tronWeb.contract().at(USDT_TRC20_CONTRACT);
      const txid: string = await contract.transfer(params.to, raw).send({
        feeLimit: 100_000_000,
        callValue: 0,
        shouldPollResponse: false,
      });
      if (!txid) throw new Error('TronLink returned no txid');
      return { txHash: txid, fromAddress, walletLabel: detectTronWalletLabel(), chain: 'tron' };
    }

    throw new Error(`Unsupported TRON currency: ${params.currency}`);
  } catch (err: any) {
    const msg = err?.message || String(err);
    if (/reject|denied|cancel|declined/i.test(msg)) {
      throw new Error('Transaction cancelled by user');
    }
    throw err instanceof Error ? err : new Error(msg);
  }
}
